// Sonner adapter: keep the same API shape but delegate to sonner's toast
import * as React from "react"
import { toast as sonner } from "@/components/ui/sonner-toast"

// Compatible input type used across the app historically
type LegacyToastVariant = "default" | "destructive" | "success" | undefined
type LegacyToastInput = {
  title?: React.ReactNode
  description?: React.ReactNode
  variant?: LegacyToastVariant
  // Legacy apps sometimes pass a `className` for styling toasts.
  // We accept it here for compatibility; the sonner adapter currently
  // ignores it, but keeping the field prevents TS errors across the app.
  className?: string
  action?: React.ReactNode
}

// Map legacy toast signature to Sonner API
function toast({ title, description, variant }: LegacyToastInput = {}) {
  const message =
    typeof title === "string" || typeof title === "number" ? (title as string) : undefined

  const descNode = description as React.ReactNode | undefined

  switch (variant) {
    case "destructive":
      return sonner.error(message ?? "Error", { description: descNode })
    case "success":
      return sonner.success(message ?? (typeof descNode === "string" ? descNode : "Éxito"), {
        description: descNode,
      })
    default:
      // default/info toast
      if (message) return sonner(message, { description: descNode })
      return sonner("", { description: descNode })
  }
}

// Hook shim to satisfy existing imports; we no longer track local state to avoid duplicate UIs
function useToast() {
  return React.useMemo(
    () => ({
      toasts: [] as any[],
      toast,
      dismiss: (id?: string | number) => sonner.dismiss(id as any),
    }),
    []
  )
}

export { useToast, toast }
